import { ExternalLink, Star } from 'lucide-react';
import { GoogleReview } from '../hooks/useProfileData';
import { cn } from '@/lib/utils';

interface FeaturedGoogleReviewsProps {
  reviews: GoogleReview[];
  reviewsUrl?: string;
  tone?: 'light' | 'dark';
  className?: string;
}

export function FeaturedGoogleReviews({
  reviews,
  reviewsUrl,
  tone = 'light',
  className
}: FeaturedGoogleReviewsProps) {
  if (reviews.length === 0) {
    return null;
  }

  const dark = tone === 'dark';

  return (
    <section className={cn('py-16 sm:py-24', className)} aria-labelledby="featured-google-reviews">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <div className="mb-10 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className={cn(
              'text-xs font-semibold uppercase tracking-[0.2em]',
              dark ? 'text-cyan-300' : 'text-primary'
            )}>
              Client perspectives
            </p>
            <h2
              id="featured-google-reviews"
              className={cn('mt-3 text-3xl font-semibold tracking-tight sm:text-4xl', dark ? 'text-white' : 'text-slate-950')}
            >
              Featured five-star Google reviews
            </h2>
            <p className={cn('mt-3 max-w-2xl text-sm', dark ? 'text-slate-400' : 'text-slate-600')}>
              A curated selection of public reviews assigned to this loan officer.
            </p>
          </div>
          {reviewsUrl && (
            <a
              href={reviewsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                'inline-flex min-h-11 items-center gap-2 text-sm font-semibold underline-offset-4 hover:underline',
                dark ? 'text-cyan-300' : 'text-primary'
              )}
            >
              View on Google <ExternalLink className="h-4 w-4" aria-hidden="true" />
            </a>
          )}
        </div>

        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {reviews.map((review) => (
            <article
              key={review.publicId}
              className={cn(
                'rounded-2xl border p-6 shadow-sm',
                dark
                  ? 'border-white/10 bg-white/[0.04] text-slate-200'
                  : 'border-slate-200 bg-white text-slate-700'
              )}
            >
              <div className="flex gap-1" aria-label={`${Math.max(0, Math.min(5, review.starRating))} out of five stars`}>
                {Array.from({ length: 5 }).map((_, index) => (
                  <Star
                    key={index}
                    className={cn(
                      'h-4 w-4 text-amber-400',
                      index < Math.max(0, Math.min(5, review.starRating)) && 'fill-amber-400'
                    )}
                    aria-hidden="true"
                  />
                ))}
              </div>
              <blockquote className="mt-5 text-sm leading-7">“{review.comment}”</blockquote>
              <p className={cn('mt-6 text-sm font-semibold', dark ? 'text-white' : 'text-slate-950')}>
                {review.reviewerDisplayName || 'Google reviewer'}
              </p>
              <p className={cn('mt-1 text-xs', dark ? 'text-slate-500' : 'text-slate-500')}>
                Google review
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
